import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

type DebounceOptions = { leading?: boolean; trailing?: boolean };

// Inspired by https://github.com/juliencrn/usehooks-ts/
export function useDebouncedValue<T>(
  value: T,
  delay: number,
  options?: DebounceOptions
) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const previousValueRef = useRef<T>(value);

  const debounced = useDebouncedCallback(setDebouncedValue, delay, options);

  useEffect(() => {
    if (value !== previousValueRef.current) {
      debounced(value);
      previousValueRef.current = value;
    }
  }, [debounced, value]);

  return debouncedValue;
}

export function useDebouncedCallback<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
  options?: { leading?: boolean; trailing?: boolean }
) {
  const debouncedFnRef = useRef<ReturnType<typeof debounce>>();

  useOnUnmount(() => {
    if (debouncedFnRef.current) {
      debouncedFnRef.current.cancel();
    }
  });

  const debounced = useMemo(() => {
    return debounce(fn, delay, options);
  }, [delay, fn, options]);

  useEffect(() => {
    if (debouncedFnRef.current) {
      debouncedFnRef.current.cancel();
    }

    debouncedFnRef.current = debounced;
  }, [debounced]);

  return debounced;
}

export function useOnUnmount(fn: () => void) {
  const fnRef = useRef(fn);

  // Always update the ref to the latest function.
  fnRef.current = fn;

  // Run the function on unmount.
  useEffect(() => () => fnRef.current(), []);
}
